/*
 * r16 TT-3 (2026-10-03): the TikTok block of the Slot Stats page - the server's contract and the labels.
 *
 * GET /uiapi/slots/{slot}/tiktok-stats (server r16 TT-3, TikTokStatsService.view; table tiktok_stats, one row per slot per day in
 * the slot's time zone) answers:
 *   { platform: 'TIKTOK' | 'INSTAGRAM' | ..., applies: bool,
 *     today, last7, last30, total: { likes, follows, storyLikes, videosWatched, watchSeconds, likesUnconfirmed,
 *                                    followsUnconfirmed, storyRingsSeen, sessions },
 *     days: [{ date: 'YYYY-MM-DD', ...the same counters }] (newest first, at most 30),
 *     recordedSince: 'YYYY-MM-DD' | null }
 * 404 (a server before r16 TT-3, or -Dnecro.server.tiktok.stats=false / the TikTok master off: {error:'not_enabled'}): no card.
 * An Instagram slot answers applies:false: no card either.
 *
 * What the counters mean (the worker, BC r16 TT-3 / TT-4 / TT-6 / TT-7 / TT-8): a like counts only when a screen read shows the PROVEN
 * liked form (TT-7: "Video liked", the heart selected - a count that moved alone is unconfirmed; TT-8: also when that form shows on the
 * retry's read of the same video); a follow counts only once the account's OWN Following count went up (TT-4 live: TikTok
 * showed "Following" on the profile but did not keep the follow - the screen alone is not proof; a private account's request counts
 * when the profile shows "Requested"). A tap whose landing was not proven is an "unconfirmed" one: never counted as a like or a
 * follow, shown apart - and so is a follow the screen showed that TikTok did not keep (TT-6: the worker counts it unconfirmed).
 * Story likes (worker TT-9, TtStoryLike; proven live in TT-10): counted when the story's next read shows it liked. r16 TT-11 (server): they
 * run only while the slot's setting 1701 ("Enable Story Likes" on TikTok) is on, and each one - confirmed or an unconfirmed tap - spends
 * the story-like limits per hour / day / week (431-446). Videos watched (worker TT-11): each video once per session - a video TikTok
 * shows again (a swipe that did not move the feed) adds its seconds to the watch time only.
 * r16 TT-12 (P2 welcome DMs; worker TtWelcomeDm, server tiktok_stats welcome_dms / welcome_dms_unconfirmed): welcomeDms = a welcome DM
 * SENT and confirmed (the sent message read back in the chat); welcomeDmsUnconfirmed = its Send was tapped but the message never read back
 * (it may have gone out): never counted as sent, it uses up the welcome-DM hour / day limits (711-714) and is never sent again. Each new
 * follower gets at most one; the followers already there at the account's first check get none. An older server sends neither: "-".
 * r16 TT-15 (P3 reposts; worker TtRepost, server tiktok_stats reposts / reposts_unconfirmed): reposts = TikTok's own Repost of a For You video
 * ("Repost to followers"), made only for a video that passes the slot's reposting settings, CONFIRMED when the video reads back as reposted;
 * repostsUnconfirmed = the repost tap was made but its result never read (it may have landed): never counted as a repost, it uses up a post
 * of the day (610 / 611) and the video is never reposted again. Uploading videos is not built. An older server sends neither: "-".
 * r16 TT-17 (P4 outbound DMs; worker TtOutboundDm, server tiktok_stats outbound_dms / outbound_dms_unconfirmed): outboundDms = an outbound DM
 * SENT and confirmed (the sent message read back in the chat) to a target the worker visited (the slot's target accounts, the followers of its
 * competitor accounts) - Instagram's "Outbound DMs Sending (Follow Module Extension)": after the target's follow, or as the target's only action
 * (755); outboundDmsUnconfirmed = its Send was tapped but the message never read back (it may have gone out): never counted as sent, never sent
 * again. Each account gets at most one. An older server sends neither: "-".
 * r16 TT-23b (P3 posting; worker TtPost, server tiktok_stats posts / posts_unconfirmed): posts = the account's next queued video (Instagram's
 * post queue of the same account name; TT-23b review round 2: a TikTok session fills none) uploaded on TikTok - CONFIRMED when a new first video shows on the account's own profile; postsUnconfirmed = Post was
 * tapped but that proof was never read (it may have been published): never counted as a post, it uses up a post of the day (610 / 611) and that
 * queued post is never uploaded again. A session posts OR reposts, never both. An older server sends neither: "-".
 */

/** The counters in the order the card shows them, with their labels and captions (V1 has no TikTok stats: these are V3's). */
export const TIKTOK_COUNTERS = [
  { key: 'likes', label: 'Likes', caption: "Counted when the next screen read proved the like (the session's first like: once the own Liked tab shows it)" },
  { key: 'follows', label: 'Follows', caption: "Counted once the account's own Following count went up (a private account: its request)" },
  { key: 'storyLikes', label: 'Story likes', caption: "Counted when the story's next read shows it liked (only while setting 1701 is on; within their own hour / day / week limits)" },
  { key: 'videosWatched', label: 'Videos watched', caption: 'Each video once per session: For You warm-up, the like check wait, story scan and before each like' },
  { key: 'watchSeconds', label: 'Watch time', caption: 'Time spent watching those videos', format: 'duration' },
];

/** The two "not counted" figures, shown apart so they are never read as done actions. */
export const TIKTOK_UNCONFIRMED = [
  { key: 'likesUnconfirmed', label: 'Unconfirmed likes' },
  { key: 'followsUnconfirmed', label: 'Unconfirmed follows' },
];

/** The periods of the figures. */
export const TIKTOK_PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'total', label: 'Total' },
];

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : (typeof v === 'string' && /^-?\d+$/.test(v.trim()) ? Number(v.trim()) : null));

/** r16 TT-9: likes the worker's like probation proved TikTok did not keep (server likesDropped). Already inside likesUnconfirmed: never summed. */
export const TIKTOK_DROPPED_KEY = 'likesDropped';

/**
 * r16 TT-12: the P2 welcome DMs - their own figure (a message, not an engagement tap: never in the likes / follows chart or the not-counted
 * taps). The unconfirmed ones are shown with it, never added to it.
 */
export const TIKTOK_WELCOME = {
  key: 'welcomeDms',
  label: 'Welcome DMs',
  caption: 'Counted when the sent message reads back in the chat: one per new follower, never a second one',
};
export const TIKTOK_WELCOME_UNCONFIRMED_KEY = 'welcomeDmsUnconfirmed';

/**
 * r16 TT-15: the P3 reposts (TikTok's own Repost of For You videos) - their own figure, as the welcome DMs: never in the likes / follows chart or
 * the not-counted likes / follows taps. The unconfirmed ones are shown with it, never added to it.
 */
export const TIKTOK_REPOST = {
  key: 'reposts',
  label: 'Reposts',
  caption: "TikTok's own Repost of a For You video, counted when the video reads back as reposted: never a second one",
};
export const TIKTOK_REPOST_UNCONFIRMED_KEY = 'repostsUnconfirmed';

/**
 * r16 TT-17: the P4 outbound DMs - their own figure, as the welcome DMs: a message, never in the likes / follows chart or the not-counted likes /
 * follows taps. The unconfirmed ones are shown with it, never added to it.
 */
export const TIKTOK_OUTBOUND = {
  key: 'outboundDms',
  label: 'Outbound DMs',
  caption: 'Counted when the sent message reads back in the chat: one per target account, never a second one',
};
export const TIKTOK_OUTBOUND_UNCONFIRMED_KEY = 'outboundDmsUnconfirmed';

/**
 * r16 TT-23b: the P3 posts (the queued video uploaded on TikTok) - their own figure, as the reposts: never in the likes / follows chart or the
 * not-counted likes / follows taps. The unconfirmed ones are shown with them, never added to them.
 */
export const TIKTOK_POST = {
  key: 'posts',
  label: 'Posts',
  caption: "The account's next queued video uploaded on TikTok, counted when a new first video shows on its own profile: never uploaded twice",
};
export const TIKTOK_POST_UNCONFIRMED_KEY = 'postsUnconfirmed';

const EXTRA_KEYS = ['storyRingsSeen', 'sessions', TIKTOK_DROPPED_KEY, TIKTOK_WELCOME.key, TIKTOK_WELCOME_UNCONFIRMED_KEY, TIKTOK_REPOST.key,
  TIKTOK_REPOST_UNCONFIRMED_KEY, TIKTOK_OUTBOUND.key, TIKTOK_OUTBOUND_UNCONFIRMED_KEY, TIKTOK_POST.key, TIKTOK_POST_UNCONFIRMED_KEY];

function counters(raw) {
  const out = {};
  const keys = [...TIKTOK_COUNTERS, ...TIKTOK_UNCONFIRMED].map((c) => c.key).concat(EXTRA_KEYS);
  for (const k of keys) out[k] = raw && typeof raw === 'object' ? num(raw[k]) : null;
  return out;
}

/** The answer, normalised; null when it is not a tiktok-stats answer at all. */
export function normaliseTikTokStats(data) {
  if (!data || typeof data !== 'object' || !('applies' in data) || !('total' in data)) return null;
  const days = Array.isArray(data.days) ? data.days.filter((d) => d && typeof d.date === 'string').map((d) => ({ date: d.date, ...counters(d) })) : [];
  return {
    platform: typeof data.platform === 'string' ? data.platform : 'INSTAGRAM',
    applies: data.applies === true,
    today: counters(data.today),
    last7: counters(data.last7),
    last30: counters(data.last30),
    total: counters(data.total),
    days,
    recordedSince: typeof data.recordedSince === 'string' ? data.recordedSince : null,
    // r16 TT-7: the slot's today as the server counts it (server TT-7; null from an older server)
    asOf: typeof data.asOf === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data.asOf) ? data.asOf : null,
  };
}

/** Seconds as a duration: "45 s", "12 min", "1 h 05 min"; '-' for a missing value. */
export function fmtDuration(seconds) {
  const s = num(seconds);
  if (s === null || s < 0) return '-';
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')} min`;
}

/** One figure as shown: a whole number with separators, or a duration. */
export function fmtCounter(counter, value) {
  if (value === null || value === undefined) return null;
  if (counter && counter.format === 'duration') return fmtDuration(value);
  return Math.round(value).toLocaleString('en-US');
}

/** True when nothing was ever recorded (a TikTok slot before its first TT-3 session). */
export function nothingRecorded(v) {
  return !v || (v.days.length === 0 && (v.total.sessions === null || v.total.sessions === 0));
}

/** r16 TT-6: the two actions the daily chart draws (the page's action colours: likes violet, follows teal). */
export const TIKTOK_CHART_SERIES = [
  { key: 'likes', label: 'Likes' },
  { key: 'follows', label: 'Follows' },
];

const addDay = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

/**
 * r16 TT-6: the days of the answer as a CONTIGUOUS series, oldest first, for the daily chart and its table twin. The server keeps one
 * row per day with a TikTok session (tiktok_stats); a day between two recorded ones had no session: it is filled with zeros and
 * marked { recorded: false } (the tooltip and the table say "no session", never a silent 0). At most `maxDays` (30) days.
 * r16 TT-7 (review round 1 should-fix): the series runs up to the slot's TODAY (the server's asOf) - the days after the newest
 * recorded one had no session and are drawn / named as such (they were left out); today is marked partial (the day is not over).
 * Without asOf (an older server) it ends at the newest recorded day, as before.
 */
export function tiktokDays(v, maxDays = 30) {
  if (!v || !v.days.length) return [];
  const byDate = new Map(v.days.map((d) => [d.date, d]));
  const dates = v.days.map((d) => d.date).sort();
  const newest = dates[dates.length - 1];
  const asOf = typeof v.asOf === 'string' ? v.asOf : null;
  const last = asOf && asOf > newest ? asOf : newest;
  let first = dates[0];
  const span = (iso) => Math.round((Date.parse(`${last}T00:00:00Z`) - Date.parse(`${iso}T00:00:00Z`)) / 86400000) + 1;
  if (span(first) > maxDays) first = addDay(last, -(maxDays - 1));
  const keys = [...TIKTOK_COUNTERS, ...TIKTOK_UNCONFIRMED].map((c) => c.key).concat(EXTRA_KEYS);
  const out = [];
  for (let day = first; day <= last; day = addDay(day, 1)) {
    const d = byDate.get(day);
    const row = { date: day, recorded: !!d, partial: false };
    for (const k of keys) row[k] = d ? d[k] : 0;
    out.push(row);
  }
  // the newest row is TODAY (the slot's day, still running) when it is the server's "today": same sessions, likes and follows
  const n = out[out.length - 1];
  const t = v.today || {};
  if (asOf) {
    if (n && n.date === asOf) n.partial = true;	// TT-7: the server named today
  } else if (n && n.recorded && typeof t.sessions === 'number' && t.sessions > 0 && n.sessions === t.sessions && n.likes === t.likes && n.follows === t.follows) {
    n.partial = true;
  }
  return out;
}

/** r16 TT-6: one KPI tile's figures: the last 7 days (the headline), today and the total ('-' when the server sent none). */
export function tiktokKpi(v, key) {
  return { last7: v.last7[key], today: v.today[key], total: v.total[key] };
}
